// src/utils/countries.js

/**
 * Country model with full dataset (ISO alpha-2 + dial code + emoji flag)
 */
export class Country {
  constructor({ name, isoCode, dialCode, flag }) {
    this.name = name;
    this.isoCode = isoCode;
    this.dialCode = dialCode;
    this.flag = flag;
  }

  toJSON() {
    return {
      name: this.name,
      isoCode: this.isoCode,
      dialCode: this.dialCode,
      flag: this.flag,
    };
  }

  // Helper method to get formatted display string
  get displayName() {
    return `${this.flag} ${this.name} (+${this.dialCode})`;
  }

  // Helper method to get country code with dial code
  get dialCodeWithPlus() {
    return `+${this.dialCode}`;
  }

  // Helper method to check if this country matches a search term
  matchesSearch(searchTerm) {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase().trim();
    return (
      this.name.toLowerCase().includes(term) ||
      this.isoCode.toLowerCase().includes(term) ||
      this.dialCode.includes(term)
    );
  }
}

/**
 * Utility to build emoji flag from ISO code (generates on the fly)
 * @param {string} isoCode - ISO alpha-2 country code
 * @returns {string} Emoji flag
 */
export const getFlagEmoji = (isoCode) => {
  if (!isoCode || isoCode.length !== 2) return '🏳️';
  const codePoints = isoCode
    .toUpperCase()
    .split('')
    .map(char => 0x1F1E6 - 65 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

/**
 * Full list of all countries
 */
export const countries = [
  new Country({ name: 'Afghanistan', isoCode: 'AF', dialCode: '93', flag: '🇦🇫' }),
  new Country({ name: 'Albania', isoCode: 'AL', dialCode: '355', flag: '🇦🇱' }),
  new Country({ name: 'Algeria', isoCode: 'DZ', dialCode: '213', flag: '🇩🇿' }),
  new Country({ name: 'American Samoa', isoCode: 'AS', dialCode: '1684', flag: '🇦🇸' }),
  new Country({ name: 'Andorra', isoCode: 'AD', dialCode: '376', flag: '🇦🇩' }),
  new Country({ name: 'Angola', isoCode: 'AO', dialCode: '244', flag: '🇦🇴' }),
  new Country({ name: 'Anguilla', isoCode: 'AI', dialCode: '1264', flag: '🇦🇮' }),
  new Country({ name: 'Antigua & Barbuda', isoCode: 'AG', dialCode: '1268', flag: '🇦🇬' }),
  new Country({ name: 'Argentina', isoCode: 'AR', dialCode: '54', flag: '🇦🇷' }),
  new Country({ name: 'Armenia', isoCode: 'AM', dialCode: '374', flag: '🇦🇲' }),
  new Country({ name: 'Aruba', isoCode: 'AW', dialCode: '297', flag: '🇦🇼' }),
  new Country({ name: 'Australia', isoCode: 'AU', dialCode: '61', flag: '🇦🇺' }),
  new Country({ name: 'Austria', isoCode: 'AT', dialCode: '43', flag: '🇦🇹' }),
  new Country({ name: 'Azerbaijan', isoCode: 'AZ', dialCode: '994', flag: '🇦🇿' }),
  new Country({ name: 'Bahamas', isoCode: 'BS', dialCode: '1242', flag: '🇧🇸' }),
  new Country({ name: 'Bahrain', isoCode: 'BH', dialCode: '973', flag: '🇧🇭' }),
  new Country({ name: 'Bangladesh', isoCode: 'BD', dialCode: '880', flag: '🇧🇩' }),
  new Country({ name: 'Barbados', isoCode: 'BB', dialCode: '1246', flag: '🇧🇧' }),
  new Country({ name: 'Belarus', isoCode: 'BY', dialCode: '375', flag: '🇧🇾' }),
  new Country({ name: 'Belgium', isoCode: 'BE', dialCode: '32', flag: '🇧🇪' }),
  new Country({ name: 'Belize', isoCode: 'BZ', dialCode: '501', flag: '🇧🇿' }),
  new Country({ name: 'Benin', isoCode: 'BJ', dialCode: '229', flag: '🇧🇯' }),
  new Country({ name: 'Bermuda', isoCode: 'BM', dialCode: '1441', flag: '🇧🇲' }),
  new Country({ name: 'Bhutan', isoCode: 'BT', dialCode: '975', flag: '🇧🇹' }),
  new Country({ name: 'Bolivia', isoCode: 'BO', dialCode: '591', flag: '🇧🇴' }),
  new Country({ name: 'Bosnia & Herzegovina', isoCode: 'BA', dialCode: '387', flag: '🇧🇦' }),
  new Country({ name: 'Botswana', isoCode: 'BW', dialCode: '267', flag: '🇧🇼' }),
  new Country({ name: 'Brazil', isoCode: 'BR', dialCode: '55', flag: '🇧🇷' }),
  new Country({ name: 'British Virgin Islands', isoCode: 'VG', dialCode: '1284', flag: '🇻🇬' }),
  new Country({ name: 'Brunei', isoCode: 'BN', dialCode: '673', flag: '🇧🇳' }),
  new Country({ name: 'Bulgaria', isoCode: 'BG', dialCode: '359', flag: '🇧🇬' }),
  new Country({ name: 'Burkina Faso', isoCode: 'BF', dialCode: '226', flag: '🇧🇫' }),
  new Country({ name: 'Burundi', isoCode: 'BI', dialCode: '257', flag: '🇧🇮' }),
  new Country({ name: 'Cambodia', isoCode: 'KH', dialCode: '855', flag: '🇰🇭' }),
  new Country({ name: 'Cameroon', isoCode: 'CM', dialCode: '237', flag: '🇨🇲' }),
  new Country({ name: 'Canada', isoCode: 'CA', dialCode: '1', flag: '🇨🇦' }),
  new Country({ name: 'Cape Verde', isoCode: 'CV', dialCode: '238', flag: '🇨🇻' }),
  new Country({ name: 'Cayman Islands', isoCode: 'KY', dialCode: '1345', flag: '🇰🇾' }),
  new Country({ name: 'Central African Republic', isoCode: 'CF', dialCode: '236', flag: '🇨🇫' }),
  new Country({ name: 'Chad', isoCode: 'TD', dialCode: '235', flag: '🇹🇩' }),
  new Country({ name: 'Chile', isoCode: 'CL', dialCode: '56', flag: '🇨🇱' }),
  new Country({ name: 'China', isoCode: 'CN', dialCode: '86', flag: '🇨🇳' }),
  new Country({ name: 'Colombia', isoCode: 'CO', dialCode: '57', flag: '🇨🇴' }),
  new Country({ name: 'Comoros', isoCode: 'KM', dialCode: '269', flag: '🇰🇲' }),
  new Country({ name: 'Congo (DRC)', isoCode: 'CD', dialCode: '243', flag: '🇨🇩' }),
  new Country({ name: 'Congo (Republic)', isoCode: 'CG', dialCode: '242', flag: '🇨🇬' }),
  new Country({ name: 'Costa Rica', isoCode: 'CR', dialCode: '506', flag: '🇨🇷' }),
  new Country({ name: 'Côte d\'Ivoire', isoCode: 'CI', dialCode: '225', flag: '🇨🇮' }),
  new Country({ name: 'Croatia', isoCode: 'HR', dialCode: '385', flag: '🇭🇷' }),
  new Country({ name: 'Cuba', isoCode: 'CU', dialCode: '53', flag: '🇨🇺' }),
  new Country({ name: 'Curaçao', isoCode: 'CW', dialCode: '599', flag: '🇨🇼' }),
  new Country({ name: 'Cyprus', isoCode: 'CY', dialCode: '357', flag: '🇨🇾' }),
  new Country({ name: 'Czechia', isoCode: 'CZ', dialCode: '420', flag: '🇨🇿' }),
  new Country({ name: 'Denmark', isoCode: 'DK', dialCode: '45', flag: '🇩🇰' }),
  new Country({ name: 'Djibouti', isoCode: 'DJ', dialCode: '253', flag: '🇩🇯' }),
  new Country({ name: 'Dominica', isoCode: 'DM', dialCode: '1767', flag: '🇩🇲' }),
  new Country({ name: 'Dominican Republic', isoCode: 'DO', dialCode: '1', flag: '🇩🇴' }),
  new Country({ name: 'Ecuador', isoCode: 'EC', dialCode: '593', flag: '🇪🇨' }),
  new Country({ name: 'Egypt', isoCode: 'EG', dialCode: '20', flag: '🇪🇬' }),
  new Country({ name: 'El Salvador', isoCode: 'SV', dialCode: '503', flag: '🇸🇻' }),
  new Country({ name: 'Equatorial Guinea', isoCode: 'GQ', dialCode: '240', flag: '🇬🇶' }),
  new Country({ name: 'Eritrea', isoCode: 'ER', dialCode: '291', flag: '🇪🇷' }),
  new Country({ name: 'Estonia', isoCode: 'EE', dialCode: '372', flag: '🇪🇪' }),
  new Country({ name: 'Eswatini', isoCode: 'SZ', dialCode: '268', flag: '🇸🇿' }),
  new Country({ name: 'Ethiopia', isoCode: 'ET', dialCode: '251', flag: '🇪🇹' }),
  new Country({ name: 'Faroe Islands', isoCode: 'FO', dialCode: '298', flag: '🇫🇴' }),
  new Country({ name: 'Fiji', isoCode: 'FJ', dialCode: '679', flag: '🇫🇯' }),
  new Country({ name: 'Finland', isoCode: 'FI', dialCode: '358', flag: '🇫🇮' }),
  new Country({ name: 'France', isoCode: 'FR', dialCode: '33', flag: '🇫🇷' }),
  new Country({ name: 'French Guiana', isoCode: 'GF', dialCode: '594', flag: '🇬🇫' }),
  new Country({ name: 'French Polynesia', isoCode: 'PF', dialCode: '689', flag: '🇵🇫' }),
  new Country({ name: 'Gabon', isoCode: 'GA', dialCode: '241', flag: '🇬🇦' }),
  new Country({ name: 'Gambia', isoCode: 'GM', dialCode: '220', flag: '🇬🇲' }),
  new Country({ name: 'Georgia', isoCode: 'GE', dialCode: '995', flag: '🇬🇪' }),
  new Country({ name: 'Germany', isoCode: 'DE', dialCode: '49', flag: '🇩🇪' }),
  new Country({ name: 'Ghana', isoCode: 'GH', dialCode: '233', flag: '🇬🇭' }),
  new Country({ name: 'Gibraltar', isoCode: 'GI', dialCode: '350', flag: '🇬🇮' }),
  new Country({ name: 'Greece', isoCode: 'GR', dialCode: '30', flag: '🇬🇷' }),
  new Country({ name: 'Greenland', isoCode: 'GL', dialCode: '299', flag: '🇬🇱' }),
  new Country({ name: 'Grenada', isoCode: 'GD', dialCode: '1473', flag: '🇬🇩' }),
  new Country({ name: 'Guadeloupe', isoCode: 'GP', dialCode: '590', flag: '🇬🇵' }),
  new Country({ name: 'Guam', isoCode: 'GU', dialCode: '1671', flag: '🇬🇺' }),
  new Country({ name: 'Guatemala', isoCode: 'GT', dialCode: '502', flag: '🇬🇹' }),
  new Country({ name: 'Guernsey', isoCode: 'GG', dialCode: '44', flag: '🇬🇬' }),
  new Country({ name: 'Guinea', isoCode: 'GN', dialCode: '224', flag: '🇬🇳' }),
  new Country({ name: 'Guinea-Bissau', isoCode: 'GW', dialCode: '245', flag: '🇬🇼' }),
  new Country({ name: 'Guyana', isoCode: 'GY', dialCode: '592', flag: '🇬🇾' }),
  new Country({ name: 'Haiti', isoCode: 'HT', dialCode: '509', flag: '🇭🇹' }),
  new Country({ name: 'Honduras', isoCode: 'HN', dialCode: '504', flag: '🇭🇳' }),
  new Country({ name: 'Hong Kong', isoCode: 'HK', dialCode: '852', flag: '🇭🇰' }),
  new Country({ name: 'Hungary', isoCode: 'HU', dialCode: '36', flag: '🇭🇺' }),
  new Country({ name: 'Iceland', isoCode: 'IS', dialCode: '354', flag: '🇮🇸' }),
  new Country({ name: 'India', isoCode: 'IN', dialCode: '91', flag: '🇮🇳' }),
  new Country({ name: 'Indonesia', isoCode: 'ID', dialCode: '62', flag: '🇮🇩' }),
  new Country({ name: 'Iran', isoCode: 'IR', dialCode: '98', flag: '🇮🇷' }),
  new Country({ name: 'Iraq', isoCode: 'IQ', dialCode: '964', flag: '🇮🇶' }),
  new Country({ name: 'Ireland', isoCode: 'IE', dialCode: '353', flag: '🇮🇪' }),
  new Country({ name: 'Isle of Man', isoCode: 'IM', dialCode: '44', flag: '🇮🇲' }),
  new Country({ name: 'Israel', isoCode: 'IL', dialCode: '972', flag: '🇮🇱' }),
  new Country({ name: 'Italy', isoCode: 'IT', dialCode: '39', flag: '🇮🇹' }),
  new Country({ name: 'Jamaica', isoCode: 'JM', dialCode: '1876', flag: '🇯🇲' }),
  new Country({ name: 'Japan', isoCode: 'JP', dialCode: '81', flag: '🇯🇵' }),
  new Country({ name: 'Jersey', isoCode: 'JE', dialCode: '44', flag: '🇯🇪' }),
  new Country({ name: 'Jordan', isoCode: 'JO', dialCode: '962', flag: '🇯🇴' }),
  new Country({ name: 'Kazakhstan', isoCode: 'KZ', dialCode: '7', flag: '🇰🇿' }),
  new Country({ name: 'Kenya', isoCode: 'KE', dialCode: '254', flag: '🇰🇪' }),
  new Country({ name: 'Kiribati', isoCode: 'KI', dialCode: '686', flag: '🇰🇮' }),
  new Country({ name: 'Kuwait', isoCode: 'KW', dialCode: '965', flag: '🇰🇼' }),
  new Country({ name: 'Kyrgyzstan', isoCode: 'KG', dialCode: '996', flag: '🇰🇬' }),
  new Country({ name: 'Laos', isoCode: 'LA', dialCode: '856', flag: '🇱🇦' }),
  new Country({ name: 'Latvia', isoCode: 'LV', dialCode: '371', flag: '🇱🇻' }),
  new Country({ name: 'Lebanon', isoCode: 'LB', dialCode: '961', flag: '🇱🇧' }),
  new Country({ name: 'Lesotho', isoCode: 'LS', dialCode: '266', flag: '🇱🇸' }),
  new Country({ name: 'Liberia', isoCode: 'LR', dialCode: '231', flag: '🇱🇷' }),
  new Country({ name: 'Libya', isoCode: 'LY', dialCode: '218', flag: '🇱🇾' }),
  new Country({ name: 'Liechtenstein', isoCode: 'LI', dialCode: '423', flag: '🇱🇮' }),
  new Country({ name: 'Lithuania', isoCode: 'LT', dialCode: '370', flag: '🇱🇹' }),
  new Country({ name: 'Luxembourg', isoCode: 'LU', dialCode: '352', flag: '🇱🇺' }),
  new Country({ name: 'Macau', isoCode: 'MO', dialCode: '853', flag: '🇲🇴' }),
  new Country({ name: 'North Macedonia', isoCode: 'MK', dialCode: '389', flag: '🇲🇰' }),
  new Country({ name: 'Madagascar', isoCode: 'MG', dialCode: '261', flag: '🇲🇬' }),
  new Country({ name: 'Malawi', isoCode: 'MW', dialCode: '265', flag: '🇲🇼' }),
  new Country({ name: 'Malaysia', isoCode: 'MY', dialCode: '60', flag: '🇲🇾' }),
  new Country({ name: 'Maldives', isoCode: 'MV', dialCode: '960', flag: '🇲🇻' }),
  new Country({ name: 'Mali', isoCode: 'ML', dialCode: '223', flag: '🇲🇱' }),
  new Country({ name: 'Malta', isoCode: 'MT', dialCode: '356', flag: '🇲🇹' }),
  new Country({ name: 'Marshall Islands', isoCode: 'MH', dialCode: '692', flag: '🇲🇭' }),
  new Country({ name: 'Martinique', isoCode: 'MQ', dialCode: '596', flag: '🇲🇶' }),
  new Country({ name: 'Mauritania', isoCode: 'MR', dialCode: '222', flag: '🇲🇷' }),
  new Country({ name: 'Mauritius', isoCode: 'MU', dialCode: '230', flag: '🇲🇺' }),
  new Country({ name: 'Mayotte', isoCode: 'YT', dialCode: '262', flag: '🇾🇹' }),
  new Country({ name: 'Mexico', isoCode: 'MX', dialCode: '52', flag: '🇲🇽' }),
  new Country({ name: 'Micronesia', isoCode: 'FM', dialCode: '691', flag: '🇫🇲' }),
  new Country({ name: 'Moldova', isoCode: 'MD', dialCode: '373', flag: '🇲🇩' }),
  new Country({ name: 'Monaco', isoCode: 'MC', dialCode: '377', flag: '🇲🇨' }),
  new Country({ name: 'Mongolia', isoCode: 'MN', dialCode: '976', flag: '🇲🇳' }),
  new Country({ name: 'Montenegro', isoCode: 'ME', dialCode: '382', flag: '🇲🇪' }),
  new Country({ name: 'Morocco', isoCode: 'MA', dialCode: '212', flag: '🇲🇦' }),
  new Country({ name: 'Mozambique', isoCode: 'MZ', dialCode: '258', flag: '🇲🇿' }),
  new Country({ name: 'Myanmar', isoCode: 'MM', dialCode: '95', flag: '🇲🇲' }),
  new Country({ name: 'Namibia', isoCode: 'NA', dialCode: '264', flag: '🇳🇦' }),
  new Country({ name: 'Nauru', isoCode: 'NR', dialCode: '674', flag: '🇳🇷' }),
  new Country({ name: 'Nepal', isoCode: 'NP', dialCode: '977', flag: '🇳🇵' }),
  new Country({ name: 'Netherlands', isoCode: 'NL', dialCode: '31', flag: '🇳🇱' }),
  new Country({ name: 'New Caledonia', isoCode: 'NC', dialCode: '687', flag: '🇳🇨' }),
  new Country({ name: 'New Zealand', isoCode: 'NZ', dialCode: '64', flag: '🇳🇿' }),
  new Country({ name: 'Nicaragua', isoCode: 'NI', dialCode: '505', flag: '🇳🇮' }),
  new Country({ name: 'Niger', isoCode: 'NE', dialCode: '227', flag: '🇳🇪' }),
  new Country({ name: 'Nigeria', isoCode: 'NG', dialCode: '234', flag: '🇳🇬' }),
  new Country({ name: 'Niue', isoCode: 'NU', dialCode: '683', flag: '🇳🇺' }),
  new Country({ name: 'North Korea', isoCode: 'KP', dialCode: '850', flag: '🇰🇵' }),
  new Country({ name: 'Northern Mariana Islands', isoCode: 'MP', dialCode: '1670', flag: '🇲🇵' }),
  new Country({ name: 'Norway', isoCode: 'NO', dialCode: '47', flag: '🇳🇴' }),
  new Country({ name: 'Oman', isoCode: 'OM', dialCode: '968', flag: '🇴🇲' }),
  new Country({ name: 'Pakistan', isoCode: 'PK', dialCode: '92', flag: '🇵🇰' }),
  new Country({ name: 'Palestine', isoCode: 'PS', dialCode: '970', flag: '🇵🇸' }),
  new Country({ name: 'Panama', isoCode: 'PA', dialCode: '507', flag: '🇵🇦' }),
  new Country({ name: 'Papua New Guinea', isoCode: 'PG', dialCode: '675', flag: '🇵🇬' }),
  new Country({ name: 'Paraguay', isoCode: 'PY', dialCode: '595', flag: '🇵🇾' }),
  new Country({ name: 'Peru', isoCode: 'PE', dialCode: '51', flag: '🇵🇪' }),
  new Country({ name: 'Philippines', isoCode: 'PH', dialCode: '63', flag: '🇵🇭' }),
  new Country({ name: 'Poland', isoCode: 'PL', dialCode: '48', flag: '🇵🇱' }),
  new Country({ name: 'Portugal', isoCode: 'PT', dialCode: '351', flag: '🇵🇹' }),
  new Country({ name: 'Puerto Rico', isoCode: 'PR', dialCode: '1', flag: '🇵🇷' }),
  new Country({ name: 'Qatar', isoCode: 'QA', dialCode: '974', flag: '🇶🇦' }),
  new Country({ name: 'Réunion', isoCode: 'RE', dialCode: '262', flag: '🇷🇪' }),
  new Country({ name: 'Romania', isoCode: 'RO', dialCode: '40', flag: '🇷🇴' }),
  new Country({ name: 'Russia', isoCode: 'RU', dialCode: '7', flag: '🇷🇺' }),
  new Country({ name: 'Rwanda', isoCode: 'RW', dialCode: '250', flag: '🇷🇼' }),
  new Country({ name: 'Saint Kitts and Nevis', isoCode: 'KN', dialCode: '1869', flag: '🇰🇳' }),
  new Country({ name: 'Saint Lucia', isoCode: 'LC', dialCode: '1758', flag: '🇱🇨' }),
  new Country({ name: 'Saint Vincent & the Grenadines', isoCode: 'VC', dialCode: '1784', flag: '🇻🇨' }),
  new Country({ name: 'Samoa', isoCode: 'WS', dialCode: '685', flag: '🇼🇸' }),
  new Country({ name: 'San Marino', isoCode: 'SM', dialCode: '378', flag: '🇸🇲' }),
  new Country({ name: 'São Tomé & Príncipe', isoCode: 'ST', dialCode: '239', flag: '🇸🇹' }),
  new Country({ name: 'Saudi Arabia', isoCode: 'SA', dialCode: '966', flag: '🇸🇦' }),
  new Country({ name: 'Senegal', isoCode: 'SN', dialCode: '221', flag: '🇸🇳' }),
  new Country({ name: 'Serbia', isoCode: 'RS', dialCode: '381', flag: '🇷🇸' }),
  new Country({ name: 'Seychelles', isoCode: 'SC', dialCode: '248', flag: '🇸🇨' }),
  new Country({ name: 'Sierra Leone', isoCode: 'SL', dialCode: '232', flag: '🇸🇱' }),
  new Country({ name: 'Singapore', isoCode: 'SG', dialCode: '65', flag: '🇸🇬' }),
  new Country({ name: 'Slovakia', isoCode: 'SK', dialCode: '421', flag: '🇸🇰' }),
  new Country({ name: 'Slovenia', isoCode: 'SI', dialCode: '386', flag: '🇸🇮' }),
  new Country({ name: 'Solomon Islands', isoCode: 'SB', dialCode: '677', flag: '🇸🇧' }),
  new Country({ name: 'Somalia', isoCode: 'SO', dialCode: '252', flag: '🇸🇴' }),
  new Country({ name: 'South Africa', isoCode: 'ZA', dialCode: '27', flag: '🇿🇦' }),
  new Country({ name: 'South Korea', isoCode: 'KR', dialCode: '82', flag: '🇰🇷' }),
  new Country({ name: 'South Sudan', isoCode: 'SS', dialCode: '211', flag: '🇸🇸' }),
  new Country({ name: 'Spain', isoCode: 'ES', dialCode: '34', flag: '🇪🇸' }),
  new Country({ name: 'Sri Lanka', isoCode: 'LK', dialCode: '94', flag: '🇱🇰' }),
  new Country({ name: 'Sudan', isoCode: 'SD', dialCode: '249', flag: '🇸🇩' }),
  new Country({ name: 'Suriname', isoCode: 'SR', dialCode: '597', flag: '🇸🇷' }),
  new Country({ name: 'Sweden', isoCode: 'SE', dialCode: '46', flag: '🇸🇪' }),
  new Country({ name: 'Switzerland', isoCode: 'CH', dialCode: '41', flag: '🇨🇭' }),
  new Country({ name: 'Syria', isoCode: 'SY', dialCode: '963', flag: '🇸🇾' }),
  new Country({ name: 'Taiwan', isoCode: 'TW', dialCode: '886', flag: '🇹🇼' }),
  new Country({ name: 'Tajikistan', isoCode: 'TJ', dialCode: '992', flag: '🇹🇯' }),
  new Country({ name: 'Tanzania', isoCode: 'TZ', dialCode: '255', flag: '🇹🇿' }),
  new Country({ name: 'Thailand', isoCode: 'TH', dialCode: '66', flag: '🇹🇭' }),
  new Country({ name: 'Timor-Leste', isoCode: 'TL', dialCode: '670', flag: '🇹🇱' }),
  new Country({ name: 'Togo', isoCode: 'TG', dialCode: '228', flag: '🇹🇬' }),
  new Country({ name: 'Tonga', isoCode: 'TO', dialCode: '676', flag: '🇹🇴' }),
  new Country({ name: 'Trinidad & Tobago', isoCode: 'TT', dialCode: '1868', flag: '🇹🇹' }),
  new Country({ name: 'Tunisia', isoCode: 'TN', dialCode: '216', flag: '🇹🇳' }),
  new Country({ name: 'Turkey', isoCode: 'TR', dialCode: '90', flag: '🇹🇷' }),
  new Country({ name: 'Turkmenistan', isoCode: 'TM', dialCode: '993', flag: '🇹🇲' }),
  new Country({ name: 'Turks & Caicos Islands', isoCode: 'TC', dialCode: '1649', flag: '🇹🇨' }),
  new Country({ name: 'Tuvalu', isoCode: 'TV', dialCode: '688', flag: '🇹🇻' }),
  new Country({ name: 'Uganda', isoCode: 'UG', dialCode: '256', flag: '🇺🇬' }),
  new Country({ name: 'Ukraine', isoCode: 'UA', dialCode: '380', flag: '🇺🇦' }),
  new Country({ name: 'United Arab Emirates', isoCode: 'AE', dialCode: '971', flag: '🇦🇪' }),
  new Country({ name: 'United Kingdom', isoCode: 'GB', dialCode: '44', flag: '🇬🇧' }),
  new Country({ name: 'United States', isoCode: 'US', dialCode: '1', flag: '🇺🇸' }),
  new Country({ name: 'Uruguay', isoCode: 'UY', dialCode: '598', flag: '🇺🇾' }),
  new Country({ name: 'Uzbekistan', isoCode: 'UZ', dialCode: '998', flag: '🇺🇿' }),
  new Country({ name: 'Vanuatu', isoCode: 'VU', dialCode: '678', flag: '🇻🇺' }),
  new Country({ name: 'Vatican City', isoCode: 'VA', dialCode: '39', flag: '🇻🇦' }),
  new Country({ name: 'Venezuela', isoCode: 'VE', dialCode: '58', flag: '🇻🇪' }),
  new Country({ name: 'Vietnam', isoCode: 'VN', dialCode: '84', flag: '🇻🇳' }),
  new Country({ name: 'Yemen', isoCode: 'YE', dialCode: '967', flag: '🇾🇪' }),
  new Country({ name: 'Zambia', isoCode: 'ZM', dialCode: '260', flag: '🇿🇲' }),
  new Country({ name: 'Zimbabwe', isoCode: 'ZW', dialCode: '263', flag: '🇿🇼' }),
];

/**
 * Sort countries alphabetically by name
 */
export const sortedCountries = [...countries].sort((a, b) => 
  a.name.localeCompare(b.name)
);

/**
 * Get a country by ISO code
 * @param {string} isoCode - ISO alpha-2 country code
 * @returns {Country|null} Country object or null if not found
 */
export const getCountryByIso = (isoCode) => {
  if (!isoCode) return null;
  const code = isoCode.toUpperCase();
  return countries.find(country => country.isoCode === code) || null;
};

/**
 * Get a country by dial code
 * @param {string} dialCode - Dial code (with or without +)
 * @returns {Country|null} Country object or null if not found
 */
export const getCountryByDialCode = (dialCode) => {
  if (!dialCode) return null;
  const code = dialCode.replace('+', '').trim();
  return countries.find(country => country.dialCode === code) || null;
};

/**
 * Search countries by name, ISO code, or dial code
 * @param {string} searchTerm - Search term
 * @returns {Country[]} Array of matching countries
 */
export const searchCountries = (searchTerm) => {
  if (!searchTerm) return sortedCountries;
  const term = searchTerm.toLowerCase().trim();
  return sortedCountries.filter(country => country.matchesSearch(term));
};

/**
 * Get countries by region (manual grouping)
 * @param {string} region - Region name
 * @returns {Country[]} Array of countries in the region
 */
export const getCountriesByRegion = (region) => {
  const regions = {
    africa: ['DZ', 'AO', 'BJ', 'BW', 'BF', 'BI', 'CV', 'CM', 'CF', 'TD', 'KM', 'CG', 'CD', 'DJ', 'EG', 'GQ', 'ER', 'ET', 'GA', 'GM', 'GH', 'GN', 'GW', 'CI', 'KE', 'LS', 'LR', 'LY', 'MG', 'MW', 'ML', 'MR', 'MU', 'MA', 'MZ', 'NA', 'NE', 'NG', 'RW', 'ST', 'SN', 'SC', 'SL', 'SO', 'ZA', 'SS', 'SD', 'SZ', 'TZ', 'TG', 'TN', 'UG', 'ZM', 'ZW'],
    asia: ['AF', 'AM', 'AZ', 'BH', 'BD', 'BT', 'BN', 'KH', 'CN', 'CY', 'GE', 'HK', 'IN', 'ID', 'IR', 'IQ', 'IL', 'JP', 'JO', 'KZ', 'KW', 'KG', 'LA', 'LB', 'MO', 'MY', 'MV', 'MN', 'MM', 'NP', 'KP', 'OM', 'PK', 'PS', 'PH', 'QA', 'SA', 'SG', 'KR', 'LK', 'SY', 'TW', 'TJ', 'TH', 'TL', 'TR', 'TM', 'AE', 'UZ', 'VN', 'YE'],
    europe: ['AL', 'AD', 'AT', 'BY', 'BE', 'BA', 'BG', 'HR', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IT', 'LV', 'LI', 'LT', 'LU', 'MK', 'MT', 'MD', 'MC', 'ME', 'NL', 'NO', 'PL', 'PT', 'RO', 'RU', 'SM', 'RS', 'SK', 'SI', 'ES', 'SE', 'CH', 'UA', 'GB', 'VA'],
    northAmerica: ['AI', 'AG', 'BS', 'BB', 'BM', 'CA', 'KY', 'CR', 'CU', 'DM', 'DO', 'SV', 'GL', 'GD', 'GP', 'GT', 'HT', 'HN', 'JM', 'MQ', 'MX', 'NI', 'PA', 'PR', 'KN', 'LC', 'VC', 'TT', 'TC', 'US'],
    southAmerica: ['AR', 'BO', 'BR', 'CL', 'CO', 'EC', 'GY', 'PY', 'PE', 'SR', 'UY', 'VE'],
    oceania: ['AS', 'AU', 'FJ', 'PF', 'GU', 'KI', 'MH', 'FM', 'NR', 'NC', 'NZ', 'NU', 'MP', 'PW', 'PG', 'WS', 'SB', 'TO', 'TV', 'VU'],
  };

  const isoCodes = regions[region.toLowerCase()] || [];
  return isoCodes.map(code => getCountryByIso(code)).filter(Boolean);
};

/**
 * Get country suggestions for phone number input
 * @param {string} phoneNumber - Phone number (partial or full)
 * @returns {Country[]} Array of matching countries
 */
export const suggestCountryByPhone = (phoneNumber) => {
  if (!phoneNumber) return sortedCountries.slice(0, 10);
  
  const cleanNumber = phoneNumber.replace(/\D/g, '');
  
  // Try to match dial code
  const matches = [];
  for (const country of sortedCountries) {
    if (cleanNumber.startsWith(country.dialCode)) {
      matches.push(country);
    }
  }
  
  return matches.length > 0 ? matches : sortedCountries.slice(0, 10);
};

/**
 * Get default country (based on browser locale or fallback to US)
 * @returns {Country} Default country
 */
export const getDefaultCountry = () => {
  try {
    // Try to get country from browser locale
    const locale = navigator.language || navigator.languages?.[0] || 'en-US';
    const parts = locale.split('-');
    const countryCode = parts.length > 1 ? parts[1] : parts[0];
    
    if (countryCode) {
      const country = getCountryByIso(countryCode.toUpperCase());
      if (country) return country;
    }
  } catch (error) {
    console.warn('Could not detect country from locale:', error);
  }
  
  // Fallback to US
  return getCountryByIso('US') || countries[0];
};

/**
 * Group countries by first letter for alphabet index
 * @returns {Object} Object with letter keys and country arrays
 */
export const groupCountriesByLetter = () => {
  const groups = {};
  
  sortedCountries.forEach(country => {
    const firstLetter = country.name.charAt(0).toUpperCase();
    if (!groups[firstLetter]) {
      groups[firstLetter] = [];
    }
    groups[firstLetter].push(country);
  });
  
  return groups;
};

/**
 * Get popular countries (for quick selection)
 * @returns {Country[]} Array of popular countries
 */
export const getPopularCountries = () => {
  const popularCodes = ['US', 'GB', 'CA', 'AU', 'DE', 'FR', 'IN', 'CN', 'JP', 'BR', 'NG', 'ZA', 'EG', 'SA', 'AE'];
  return popularCodes
    .map(code => getCountryByIso(code))
    .filter(Boolean);
};

// Default export for convenience
export default {
  countries,
  sortedCountries,
  Country,
  getCountryByIso,
  getCountryByDialCode,
  searchCountries,
  getCountriesByRegion,
  suggestCountryByPhone,
  getDefaultCountry,
  groupCountriesByLetter,
  getPopularCountries,
  getFlagEmoji,
};